// Service API pour les favoris (remplace favoritesService.ts avec localStorage)
import api from '../config/api';
import { Favorite } from './favoritesService';

export interface FavoriteApi extends Omit<Favorite, 'id' | 'timestamp'> {
  id: number;
  timestamp: string;
}

class FavoritesApiService {
  private cache: Favorite[] | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

  // Ajouter un favori
  async addFavorite(favorite: Omit<Favorite, 'id' | 'timestamp'>): Promise<void> {
    try {
      const payload = {
        type: favorite.type,
        title: favorite.title,
        content: favorite.content,
        arabic: favorite.arabic,
        reference: favorite.reference,
        category: favorite.category,
      };

      await api.post('/chat/favorites/', payload);
      this.invalidateCache();
      console.log(`[Favorites] Favori ajouté: ${favorite.title}`);
    } catch (error: any) {
      // Si le favori existe déjà (409), c'est OK
      if (error.response?.status === 400 && error.response?.data?.non_field_errors) {
        console.log('Favori déjà existant');
        return;
      }
      console.error('Erreur lors de l\'ajout du favori:', error);
      // Fallback vers localStorage
      this.addToLocalStorage(favorite);
    }
  }

  // Supprimer un favori
  async removeFavorite(id: string): Promise<void> {
    try {
      await api.delete(`/chat/favorites/${id}/`);
      this.invalidateCache();
      console.log(`[Favorites] Favori supprimé: ${id}`);
    } catch (error) {
      console.error('Erreur lors de la suppression du favori:', error);
      // Fallback vers localStorage
      this.removeFromLocalStorage(id);
    }
  }

  // Obtenir tous les favoris (avec cache)
  async getAllFavorites(): Promise<Favorite[]> {
    const now = Date.now();

    // Utiliser le cache si valide
    if (this.cache && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
      return this.cache;
    }

    try {
      const response = await api.get('/chat/favorites/');
      const favorites = response.data.results || response.data || [];
      this.cache = Array.isArray(favorites) ? favorites.map(this.mapToFavorite) : [];
      this.cacheTimestamp = now;
      return this.cache;
    } catch (error) {
      console.error('Erreur lors du chargement des favoris:', error);
      // Fallback vers localStorage
      return this.getAllFromLocalStorage();
    }
  }

  // Obtenir les favoris par type
  async getFavoritesByType(type: Favorite['type']): Promise<Favorite[]> {
    try {
      const response = await api.get('/chat/favorites/', {
        params: { type }
      });
      const favorites = response.data.results || response.data;
      return favorites.map(this.mapToFavorite);
    } catch (error) {
      console.error('Erreur lors du chargement par type:', error);
      const all = await this.getAllFavorites();
      return all.filter((f) => f.type === type);
    }
  }

  // Vérifier si un élément est en favori
  async isFavorite(content: string, type: Favorite['type']): Promise<boolean> {
    try {
      const response = await api.get('/chat/favorites/check/', {
        params: { content, type }
      });
      return response.data.is_favorite || false;
    } catch (error) {
      console.error('Erreur lors de la vérification:', error);
      // Fallback vers localStorage
      const favorites = await this.getAllFavorites();
      return favorites.some((f) => f.content === content && f.type === type);
    }
  }

  // Obtenir un favori par ID
  async getFavoriteById(id: string): Promise<Favorite | null> {
    try {
      const response = await api.get(`/chat/favorites/${id}/`);
      return this.mapToFavorite(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement du favori:', error);
      const favorites = await this.getAllFavorites();
      return favorites.find((f) => f.id === id) || null;
    }
  }

  // Mapper le favori API vers Favorite
  private mapToFavorite(favorite: FavoriteApi): Favorite {
    return {
      id: favorite.id.toString(),
      type: favorite.type,
      title: favorite.title,
      content: favorite.content,
      arabic: favorite.arabic,
      reference: favorite.reference,
      timestamp: new Date(favorite.timestamp),
      category: favorite.category,
    };
  }

  // Invalider le cache
  invalidateCache(): void {
    this.cache = null;
    this.cacheTimestamp = 0;
  }

  // Fallback vers localStorage
  private addToLocalStorage(favorite: Omit<Favorite, 'id' | 'timestamp'>): void {
    try {
      const favorites = this.getAllFromLocalStorage();
      const exists = favorites.some(
        (f) => f.content === favorite.content && f.type === favorite.type
      );
      if (!exists) {
        const newFavorite: Favorite = {
          ...favorite,
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
          timestamp: new Date(),
        };
        favorites.push(newFavorite);
        localStorage.setItem(
          'chat_favorites',
          JSON.stringify(
            favorites.map((f) => ({
              ...f,
              timestamp: f.timestamp.toISOString(),
            }))
          )
        );
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout dans localStorage:', error);
    }
  }

  private removeFromLocalStorage(id: string): void {
    try {
      const favorites = this.getAllFromLocalStorage();
      const filtered = favorites.filter((f) => f.id !== id);
      localStorage.setItem(
        'chat_favorites',
        JSON.stringify(
          filtered.map((f) => ({
            ...f,
            timestamp: f.timestamp.toISOString(),
          }))
        )
      );
    } catch (error) {
      console.error('Erreur lors de la suppression dans localStorage:', error);
    }
  }

  private getAllFromLocalStorage(): Favorite[] {
    try {
      const saved = localStorage.getItem('chat_favorites');
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.map((f: any) => ({
          ...f,
          timestamp: new Date(f.timestamp),
        }));
      }
    } catch (error) {
      console.error('Erreur lors du chargement depuis localStorage:', error);
    }
    return [];
  }
}

export const favoritesApiService = new FavoritesApiService();


